/**
 * CRA Filing — produces a T776 preparer's worksheet for landlords and
 * a rent receipt for tenants. Both download as PDF.
 *
 * Single page, role-aware: shows landlord OR tenant flow based on the
 * current user's role.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select } from "@/components/ui/Primitives";
import { FileDown, Plus, Receipt, Trash2, TrendingUp, TrendingDown, Calculator } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const T776_CATEGORIES = [
  { value: "advertising", label: "Advertising" },
  { value: "insurance", label: "Insurance" },
  { value: "interest_and_bank", label: "Interest & bank charges" },
  { value: "office", label: "Office expenses" },
  { value: "professional_fees", label: "Professional fees" },
  { value: "management_and_admin", label: "Management & admin" },
  { value: "repairs_and_maintenance", label: "Repairs & maintenance" },
  { value: "salaries", label: "Salaries, wages, benefits" },
  { value: "property_taxes", label: "Property taxes" },
  { value: "travel", label: "Travel" },
  { value: "utilities", label: "Utilities" },
  { value: "motor_vehicle", label: "Motor vehicle" },
  { value: "other", label: "Other" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

function fmtCAD(cents) {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format((cents || 0) / 100);
}

export default function CRAFiling() {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id || user?.email || "";
  const role = (user?.role || user?.user_role || "landlord").toLowerCase();
  const isTenant = role === "tenant";

  return (
    <div>
      <PageHeader
        title={isTenant ? "Rent receipt & CRA filing" : "T776 — landlord tax worksheet"}
        subtitle={isTenant ? "Download a rent receipt for your taxes — auto-built from your rent payments." : "Aggregate your rent income + expenses into a T776 preparer's worksheet ready for filing."}
      />
      {isTenant ? <TenantView uid={uid}/> : <LandlordView uid={uid}/>}
    </div>
  );
}

// ─────────────────────────── LANDLORD ──────────────────────────
function LandlordView({ uid }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openExp, setOpenExp] = useState(false);
  const [eform, setEform] = useState({ category: "repairs_and_maintenance", amount_cad: "", description: "", paid_at: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        fetch(`${API}/api/cra/landlord-summary?year=${year}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } }).then(r => r.json()),
        fetch(`${API}/api/cra/expenses?year=${year}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } }).then(r => r.json()),
      ]);
      setSummary(s);
      setExpenses(e.items || []);
    } finally { setLoading(false); }
  }, [uid, year]);
  useEffect(() => { load(); }, [load]);

  async function addExp(ev) {
    ev.preventDefault();
    if (!eform.amount_cad) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cra/expenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
        body: JSON.stringify({
          year, category: eform.category,
          amount_cents: Math.round(Number(eform.amount_cad) * 100),
          description: eform.description || null,
          paid_at: eform.paid_at ? new Date(eform.paid_at).toISOString() : null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not add");
      toast.success("Expense added");
      setOpenExp(false);
      setEform({ category: "repairs_and_maintenance", amount_cad: "", description: "", paid_at: "" });
      load();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function removeExp(eid) {
    const target = expenses.find((x) => x.id === eid);
    const ok = await confirmDialog({
      title: "Delete this expense?",
      description: `${target?.description || "Expense"} · ${target ? fmtCAD(target.amount_cents) : ""}\n\nThis action cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep",
      destructive: true,
    });
    if (!ok) return;
    try {
      const r = await fetch(`${API}/api/cra/expenses/${eid}`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${r.status}`);
      }
      toast.success("Expense deleted");
      load();
    } catch (err) {
      toast.error(`Could not delete: ${err.message || err}`);
    }
  }

  function downloadPdf() {
    const url = `${API}/api/cra/landlord-summary/pdf?year=${year}&user_id=${encodeURIComponent(uid)}`;
    window.open(url, "_blank");
  }

  if (loading || !summary) return <Loader/>;
  const net = summary.net_rental_income_cents;
  const netTone = net >= 0 ? "green" : "red";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        {YEARS.map(y => (
          <button key={y} onClick={() => setYear(y)} data-testid={`year-${y}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${year === y ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            Tax year {y}
          </button>
        ))}
        <div className="flex-1"/>
        <Button onClick={downloadPdf} data-testid="download-t776"><FileDown className="w-4 h-4"/> Download T776 worksheet (PDF)</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3"/> Gross rental income</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-1" data-testid="gross-income">{fmtCAD(summary.gross_rental_income_cents)}</div>
          <div className="text-xs text-slate-500 mt-1">{summary.rent_payment_count} payment{summary.rent_payment_count === 1 ? "" : "s"}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 inline-flex items-center gap-1"><TrendingDown className="w-3 h-3"/> Total expenses</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-1" data-testid="total-expenses">{fmtCAD(summary.total_expenses_cents)}</div>
          <div className="text-xs text-slate-500 mt-1">{expenses.length} line{expenses.length === 1 ? "" : "s"}</div>
        </Card>
        <Card className={`p-4 ${net >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          <div className={`text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 ${net >= 0 ? "text-emerald-800" : "text-rose-800"}`}><Calculator className="w-3 h-3"/> Net rental income</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-1" data-testid="net-income">{fmtCAD(net)}</div>
          <Pill tone={netTone}>{net >= 0 ? "Reportable income" : "Net loss — may offset other income"}</Pill>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-slate-900">Expense ledger — {year}</h3>
          <Button onClick={() => setOpenExp(true)} className="!py-1.5 !px-3 text-xs" data-testid="add-expense"><Plus className="w-3.5 h-3.5"/> Add expense</Button>
        </div>
        {expenses.length === 0 ? (
          <EmptyState icon={Receipt} title="No expenses logged yet" description="Track every receipt as you go — they auto-aggregate by T776 category."/>
        ) : (
          <table className="w-full text-sm" data-testid="expense-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr><th className="py-2">Date</th><th>Category</th><th>Description</th><th className="text-right">Amount</th><th></th></tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id} className="border-b border-slate-50" data-testid={`expense-${e.id}`}>
                  <td className="py-2.5 text-slate-600 text-xs">{e.paid_at ? new Date(e.paid_at).toLocaleDateString("en-CA") : "—"}</td>
                  <td className="text-xs"><Pill tone="slate">{T776_CATEGORIES.find(c => c.value === e.category)?.label || e.category}</Pill></td>
                  <td className="text-slate-700">{e.description || "—"}</td>
                  <td className="text-right font-bold tabular-nums text-slate-900">{fmtCAD(e.amount_cents)}</td>
                  <td className="text-right"><button onClick={() => removeExp(e.id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded" title="Delete" data-testid={`expense-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5"/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="font-display font-bold text-slate-900 mb-3">By T776 category</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {summary.expenses_by_category.filter(c => c.amount_cents > 0).map(c => (
            <div key={c.category} className="flex items-center justify-between border-b border-slate-100 py-2" data-testid={`cat-${c.category}`}>
              <span className="text-sm text-slate-700">{c.label}</span>
              <span className="font-bold tabular-nums text-slate-900">{fmtCAD(c.amount_cents)}</span>
            </div>
          ))}
          {summary.expenses_by_category.every(c => c.amount_cents === 0) && (
            <div className="text-sm text-slate-400 text-center col-span-full py-3">No category totals yet.</div>
          )}
        </div>
      </Card>

      <Modal open={openExp} onClose={() => setOpenExp(false)} title="Add expense" testid="expense-modal">
        <form onSubmit={addExp} className="space-y-4">
          <Select label="Category" value={eform.category} onChange={(e) => setEform({...eform, category: e.target.value})} testid="expense-category">
            {T776_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Select>
          <Input label="Amount (CAD)" required type="number" step="0.01" min="0.01" value={eform.amount_cad} onChange={(e) => setEform({...eform, amount_cad: e.target.value})} testid="expense-amount"/>
          <Input label="Description" placeholder="e.g. Toilet repair — 123 Bloor St" value={eform.description} onChange={(e) => setEform({...eform, description: e.target.value})}/>
          <Input label="Paid date" type="date" value={eform.paid_at} onChange={(e) => setEform({...eform, paid_at: e.target.value})}/>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpenExp(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="expense-save">{busy ? "Saving…" : "Add expense"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ─────────────────────────── TENANT ──────────────────────────
function TenantView({ uid }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/cra/tenant-summary?year=${year}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid } });
      const d = await r.json();
      setSummary(d);
    } finally { setLoading(false); }
  }, [uid, year]);
  useEffect(() => { load(); }, [load]);

  function downloadPdf() {
    const url = `${API}/api/cra/tenant-summary/pdf?year=${year}&user_id=${encodeURIComponent(uid)}`;
    window.open(url, "_blank");
  }

  if (loading || !summary) return <Loader/>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        {YEARS.map(y => (
          <button key={y} onClick={() => setYear(y)} data-testid={`year-${y}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${year === y ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
            Tax year {y}
          </button>
        ))}
        <div className="flex-1"/>
        <Button onClick={downloadPdf} disabled={summary.payment_count === 0} data-testid="download-receipt"><FileDown className="w-4 h-4"/> Download rent receipt (PDF)</Button>
      </div>

      <Card className="p-5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-blue-700">Total rent paid in {year}</div>
        <div className="font-display text-4xl font-extrabold text-slate-900 mt-1" data-testid="rent-paid">{fmtCAD(summary.rent_paid_cents)}</div>
        <div className="text-sm text-slate-600 mt-1">{summary.payment_count} payment{summary.payment_count === 1 ? "" : "s"} to {summary.landlord?.name || "—"} {summary.landlord?.province ? `· ${summary.landlord.province}` : ""}</div>
      </Card>

      {summary.payment_count === 0 ? (
        <EmptyState icon={Receipt} title="No rent payments yet" description={`We don't see any rent paid via Central Rentals in ${year}. Once you pay through the platform, your receipt builds automatically.`}/>
      ) : (
        <Card className="p-5">
          <h3 className="font-display font-bold text-slate-900 mb-3">By month</h3>
          <table className="w-full text-sm" data-testid="rent-by-month">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="py-2">Period</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {Object.keys(summary.rent_by_period).sort().map(p => (
                <tr key={p} className="border-b border-slate-50">
                  <td className="py-2.5 text-slate-700">{p}</td>
                  <td className="text-right font-bold tabular-nums">{fmtCAD(summary.rent_by_period[p])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {summary.on_ben_eligible && (
        <Card className="p-5 bg-blue-50 border-blue-200">
          <h3 className="font-display font-bold text-blue-900">Ontario Trillium Benefit (ON-BEN)</h3>
          <p className="text-sm text-blue-900 mt-1">{summary.on_ben_hint}</p>
        </Card>
      )}
    </div>
  );
}
